import { useState, useMemo } from "react";
import {
  usePoolData,
  type PoolParameterWithValue,
} from "../../hooks/usePoolData";

// Configuración de rangos correctos para parámetros de sal
const SALT_PARAMS_CONFIG: {
  [key: string]: {
    name: string;
    optimalMin: number;
    optimalMax: number;
    warningMin: number;
    warningMax: number;
    icon: string;
  };
} = {
  salt: {
    name: "Cloro Salino",
    optimalMin: 4,
    optimalMax: 5,
    warningMin: 3,
    warningMax: 6,
    icon: "ri-drop-line",
  },
  salinity: {
    name: "Salinidad Total",
    optimalMin: 2700,
    optimalMax: 3400,
    warningMin: 2500,
    warningMax: 3600,
    icon: "ri-contrast-drop-line",
  },
};

export default function Dashboard() {
  const {
    pools,
    selectedPool,
    parameters: rawParameters,
    latestMeasurement,
    loading,
    error,
    selectPool,
    refresh,
  } = usePoolData();

  const [showAlertDetails, setShowAlertDetails] = useState(false);

  // Normalizar parámetros de sal con rangos correctos
  const parameters = useMemo(() => {
    return rawParameters.map((param) => {
      const config = SALT_PARAMS_CONFIG[param.parameterId];
      if (config) {
        // Corregir nombre y rangos si están incorrectos
        const needsCorrection =
          (param.parameterId === "salt" && param.optimalMax > 100) ||
          (param.parameterId === "salinity" && param.optimalMax < 1000);

        if (needsCorrection) {
          // Recalcular status con los rangos correctos
          let status: "optimal" | "warning" | "critical" = "optimal";
          if (param.currentValue !== null) {
            if (
              param.currentValue >= config.optimalMin &&
              param.currentValue <= config.optimalMax
            ) {
              status = "optimal";
            } else if (
              param.currentValue >= config.warningMin &&
              param.currentValue <= config.warningMax
            ) {
              status = "warning";
            } else {
              status = "critical";
            }
          }

          return {
            ...param,
            name: config.name,
            icon: config.icon,
            optimalMin: config.optimalMin,
            optimalMax: config.optimalMax,
            warningMin: config.warningMin,
            warningMax: config.warningMax,
            status,
          };
        }
      }
      return param;
    });
  }, [rawParameters]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "optimal":
        return "border-green-500 bg-green-50";
      case "warning":
        return "border-yellow-500 bg-yellow-50";
      case "critical":
        return "border-red-500 bg-red-50";
      default:
        return "border-gray-300 bg-gray-50";
    }
  };

  const getStatusIconColor = (status: string) => {
    switch (status) {
      case "optimal":
        return "text-green-500";
      case "warning":
        return "text-yellow-500";
      case "critical":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  const getProgressPercentage = (
    value: number | null,
    min: number,
    max: number,
  ) => {
    if (value === null) return 50;
    return Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);
  };

  // Count parameters by status
  const optimalCount = parameters.filter((p) => p.status === "optimal").length;
  const warningCount = parameters.filter((p) => p.status === "warning").length;
  const criticalCount = parameters.filter(
    (p) => p.status === "critical",
  ).length;

  // Get out of range parameters
  const outOfRangeParams = parameters.filter((p) => p.status !== "optimal");

  // Format timestamp
  const formatDate = (timestamp: string | undefined) => {
    if (!timestamp) return { date: "--", time: "--" };
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const measurementTime = formatDate(latestMeasurement?.timestamp);

  // Loading state
  if (loading && pools.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando datos...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error && pools.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50 min-h-screen flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-500 text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Error de conexión
          </h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </section>
    );
  }

  // No pools state
  if (pools.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50 min-h-screen flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-water-flash-line text-teal-500 text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Sin piscinas
          </h2>
          <p className="text-slate-600">
            No hay piscinas registradas en el sistema.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="dashboard"
      className="py-20 bg-gradient-to-br from-slate-50 to-teal-50 min-h-screen"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-lg p-12 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-800 mb-2">
                Estado Actual de la Piscina
              </h1>
              <p className="text-slate-600 text-lg">
                Monitoreo en tiempo real de parámetros químicos
              </p>
            </div>
            <div className="mt-4 lg:mt-0 flex items-center space-x-4">
              <select
                value={selectedPool?.id || ""}
                onChange={(e) => selectPool(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                {pools.map((pool) => (
                  <option key={pool.id} value={pool.id}>
                    {pool.name}
                  </option>
                ))}
              </select>
              <button
                onClick={refresh}
                disabled={loading}
                className="p-2 text-teal-500 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <i
                  className={`ri-refresh-line text-xl ${loading ? "animate-spin" : ""}`}
                ></i>
              </button>
              <div className="text-sm text-gray-500 whitespace-nowrap">
                <div>{measurementTime.date}</div>
                <div className="font-semibold">{measurementTime.time}</div>
              </div>
            </div>
          </div>

          {/* Critical Alerts Banner */}
          {(criticalCount > 0 || warningCount > 0) && (
            <div
              className={`mb-8 rounded-2xl p-6 ${criticalCount > 0 ? "bg-gradient-to-r from-red-500 to-red-600" : "bg-gradient-to-r from-amber-500 to-amber-600"}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center ${criticalCount > 0 ? "bg-red-400/30" : "bg-amber-400/30"}`}
                  >
                    <i
                      className={`${criticalCount > 0 ? "ri-alarm-warning-fill" : "ri-alert-fill"} text-white text-3xl`}
                    ></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">
                      {criticalCount > 0
                        ? "¡Atención Requerida!"
                        : "Advertencias Detectadas"}
                    </h3>
                    <p className="text-white/90">
                      {criticalCount > 0 &&
                        `${criticalCount} parámetro(s) en estado crítico. `}
                      {warningCount > 0 &&
                        `${warningCount} parámetro(s) requieren atención.`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAlertDetails(!showAlertDetails)}
                  className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i
                    className={`ri-arrow-${showAlertDetails ? "up" : "down"}-s-line mr-2`}
                  ></i>
                  {showAlertDetails ? "Ocultar" : "Ver Detalles"}
                </button>
              </div>

              {/* Alert Details */}
              {showAlertDetails && outOfRangeParams.length > 0 && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {outOfRangeParams.map((param) => (
                    <div
                      key={param.id}
                      className={`p-4 rounded-xl ${param.status === "critical" ? "bg-red-400/30" : "bg-amber-400/30"}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <i
                            className={`${param.icon || "ri-test-tube-line"} text-white text-xl`}
                          ></i>
                          <span className="text-white font-semibold">
                            {param.name}
                          </span>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-bold ${
                            param.status === "critical"
                              ? "bg-red-200 text-red-800"
                              : "bg-amber-200 text-amber-800"
                          }`}
                        >
                          {param.status === "critical"
                            ? "CRÍTICO"
                            : "ADVERTENCIA"}
                        </span>
                      </div>
                      <div className="text-white/90 text-sm">
                        <p>
                          Valor actual:{" "}
                          <strong>
                            {param.currentValue ?? "--"} {param.unit}
                          </strong>
                        </p>
                        <p>
                          Rango óptimo:{" "}
                          <strong>
                            {param.optimalMin}-{param.optimalMax} {param.unit}
                          </strong>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* No measurements warning */}
          {!loading && !latestMeasurement && parameters.length > 0 && (
            <div className="mb-8 rounded-2xl p-6 bg-gradient-to-r from-blue-500 to-teal-500">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center bg-white/20 flex-shrink-0">
                  <i className="ri-information-line text-white text-3xl"></i>
                </div>
                <div className="flex-1 py-1">
                  <h3 className="text-white font-bold text-lg mb-1">
                    Base de datos vacía
                  </h3>
                  <p className="text-white/90 text-sm mb-3">
                    No hay mediciones registradas para esta piscina. Comienza
                    registrando la primera medición en la sección de Registro.
                  </p>
                  <a
                    href="/registro"
                    className="inline-block px-4 py-2 bg-white text-teal-600 font-semibold rounded-lg hover:bg-white/90 transition-colors"
                  >
                    Ir a Registro
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Parameter Cards Grid */}
          {parameters.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
              {parameters.map((param) => (
                <div
                  key={param.id}
                  className={`relative p-4 rounded-2xl border-2 transition-all duration-300 hover:scale-105 cursor-pointer ${getStatusColor(param.status)} ${
                    param.status === "critical" ? "animate-pulse" : ""
                  }`}
                >
                  {param.status !== "optimal" && (
                    <div
                      className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                        param.status === "critical"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    >
                      <i className="ri-alert-fill text-white text-xs"></i>
                    </div>
                  )}
                  <div
                    className={`w-8 h-8 flex items-center justify-center mb-3 ${getStatusIconColor(param.status)}`}
                  >
                    <i
                      className={`${param.icon || "ri-test-tube-line"} text-2xl`}
                    ></i>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-slate-800 mb-1">
                      {param.currentValue ?? "--"}
                      <span className="text-sm font-normal text-slate-500 ml-1">
                        {param.unit}
                      </span>
                    </div>
                    <div className="text-xs uppercase tracking-wider text-slate-600 font-medium mb-3">
                      {param.name}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className={`h-1 rounded-full transition-all duration-500 ${
                          param.status === "optimal"
                            ? "bg-green-500"
                            : param.status === "warning"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                        style={{
                          width: `${getProgressPercentage(param.currentValue, param.optimalMin, param.optimalMax)}%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {param.optimalMin}-{param.optimalMax} {param.unit}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <i className="ri-flask-line text-4xl mb-2"></i>
              <p>No hay parámetros configurados</p>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                Parámetros Óptimos
              </h3>
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-green-500 text-xl"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-green-500 mb-2">
              {optimalCount}/{parameters.length}
            </div>
            <p className="text-slate-600 text-sm">
              {parameters.length > 0
                ? `${((optimalCount / parameters.length) * 100).toFixed(1)}% en rango ideal`
                : "Sin datos"}
            </p>
            {parameters.length > 0 && (
              <div className="mt-4 bg-green-100 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{
                    width: `${(optimalCount / parameters.length) * 100}%`,
                  }}
                ></div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                Advertencias
              </h3>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${warningCount > 0 ? "bg-amber-100" : "bg-gray-100"}`}
              >
                <i
                  className={`ri-alert-line text-xl ${warningCount > 0 ? "text-amber-500" : "text-gray-400"}`}
                ></i>
              </div>
            </div>
            <div
              className={`text-3xl font-bold mb-2 ${warningCount > 0 ? "text-amber-500" : "text-gray-400"}`}
            >
              {warningCount}
            </div>
            <p className="text-slate-600 text-sm">
              {warningCount > 0 ? "Requieren atención" : "Sin advertencias"}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Críticos</h3>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${criticalCount > 0 ? "bg-red-100 animate-pulse" : "bg-gray-100"}`}
              >
                <i
                  className={`ri-alarm-warning-line text-xl ${criticalCount > 0 ? "text-red-500" : "text-gray-400"}`}
                ></i>
              </div>
            </div>
            <div
              className={`text-3xl font-bold mb-2 ${criticalCount > 0 ? "text-red-500" : "text-gray-400"}`}
            >
              {criticalCount}
            </div>
            <p className="text-slate-600 text-sm">
              {criticalCount > 0
                ? "¡Acción inmediata!"
                : "Sin alertas críticas"}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                Última Medición
              </h3>
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-time-line text-teal-500 text-xl"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-slate-800 mb-2">
              {measurementTime.time}
            </div>
            <p className="text-slate-600 text-sm">{measurementTime.date}</p>
            {latestMeasurement?.source && (
              <p className="text-teal-600 text-sm mt-2 font-medium">
                Fuente: {latestMeasurement.source}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
